<template>
    <input
        type="text"
        v-model="val"
        @blur="isPickerOpen = false"
        @focus="closePicker"
        @mousedown="closePicker"
    >
</template>

<script>
    import 'flatpickr';
    import invert from 'lodash/invert';
    import extend from 'lodash/extend';
    import moment from '../../modules/moment';
    import debounce from 'lodash/debounce';

    import('../../../less/components/flatpickr.less');

    export default {
        name: 'datepicker',

        beforeDestroy() {
            this.instance.destroy();
        },

        props: {
            options: {
                type: Object,
                default() {
                    return {};
                },
            },

            value: {
                default() {
                    return '';
                },
            },
        },

        data() {
            return {
                val: null,
                instance: null,
                isPickerOpen: false,
            }
        },

        watch: {
            value(val) {
                this.val = val;
                this.instance.redraw();
            },

            options() {
                this.createDatepicker()
            },
        },

        mounted() {
            this.createDatepicker();

            bus.$on('openDatePicker', (obj) => {
                if (this.$el.getAttribute('id') === obj.id) {
                    this.instance.open();
                }
            });
        },

        methods: {
            /**
             * When opening the picker by click and When opening the picker by focus
             * It will close the picker by clicking the input itself.
             */
            closePicker: debounce(function() {
                if (this.isPickerOpen) {
                    this.instance.close();
                    this.$el.blur();
                    this.isPickerOpen = false;
                } else {
                    this.isPickerOpen = true;
                }
            }, 100),

            /**
             * Convert a JS date string to the moment alternative
             * @param {string} format - vanilla JS date format
             * @param {boolean} [inverse=false] - invert the lookup
             */
            convertDateFormat(format, inverse = false) {
                let conversion = {
                    // Vanilla formats
                    l: 'dddd',
                    D: 'ddd',
                    d: 'DD',
                    J: 'Do',
                    j: 'D',
                    w: 'd',
                    F: 'MMMM',
                    M: 'MMM',
                    m: 'MM',
                    n: 'M',
                    U: 'X',
                    Y: 'YYYY',
                    y: 'YY',
                };

                if (inverse) {
                    conversion = invert(conversion);
                }

                Object.keys(conversion).forEach((symbol) => {
                    format = format.replace(new RegExp(symbol, "g"), conversion[symbol]);
                });

                return format;
            },

            /**
             * Convert the date into the format to be submitted to the API
             * @param {string} date
             */
            convertForApi(date) {
                return moment(date, this.convertDateFormat('DD-MM-YYYY', true)).format('DD-MM-YYYY');
            },

            /**
             * Convert the date into the format to be output in the datepicker
             * @param {string} date
             * @param {string} format
             */
            convertForOutput(date, format) {
                return moment(date, this.convertDateFormat(format)).format(format);
            },

            /**
             * Setup the datepicker instance
             */
            createDatepicker() {
                const rental = this.$store.state.search.data.rental;

                const options = extend({
                    dateFormat: 'd-m-Y',
                    disableMobile: "true",
                    // TODO: Decouple this logic and put it in the per-component config, not directly in the reusable component - @becca
                    minDate: (rental === 'short') ? null : new moment().add(2, 'days').format('DD-MM-YYYY'),
                    maxDate: (rental === 'short') ? new moment().add(9, 'months').format('DD-MM-YYYY') : new moment().add(3, 'months').format('DD-MM-YYYY'),
                }, this.options, {
                    onChange: (selectedDates, dateStr) => {
                        this.$emit('input', dateStr);
                    }
                });

                if (typeof options.appendTo === 'function') {
                    options.appendTo = options.appendTo();
                }

                if (this.value) {
                    options.defaultDate = this.value;
                }

                this.val = this.value;

                this.instance = $(this.$el).flatpickr(options);
            },

            /**
             * Returns root element of the datepicker widget if datepicker is opened.
             * @returns {HTMLElement|null}
             */
            getPickerContainerEl() {
                return this.instance ? this.instance.calendarContainer : null;
            },
        },
    }
</script>

<style lang="less" scoped>
    input {
        cursor: pointer;
    }
</style>
